import {
  Condition,
  CodeableConcept,
  Patient,
  Encounter,
} from '@medplum/fhirtypes';
import { createReference, addProfileToResource } from '@medplum/core';
import { HL7 } from '@consts/systems.consts';

// =============================================================================
// TYPES
// =============================================================================

export type ConditionInputItem = {
  encounterDiagnosisId?: string;
  problemListItemId?: string;
  code: CodeableConcept | undefined;
  clinicalStatus: CodeableConcept | undefined;
  problemListItem: boolean;
  onsetDate: Date | null;
  notes: string;
};

// =============================================================================
// GETTERS - Safe access to nested fields
// =============================================================================

export function getConditionCode(
  condition: ConditionInputItem
): CodeableConcept | undefined {
  return condition.code;
}

export function getConditionCodeText(
  condition: ConditionInputItem
): string | undefined {
  const code = getConditionCode(condition);
  return code?.text || code?.coding?.[0]?.display;
}

export function getClinicalStatus(
  condition: ConditionInputItem
): CodeableConcept | undefined {
  return condition.clinicalStatus;
}

export function getOnsetDate(condition: ConditionInputItem): Date | null {
  return condition.onsetDate;
}

export function getNotes(condition: ConditionInputItem): string {
  return condition.notes || '';
}

export function getProblemListItem(condition: ConditionInputItem): boolean {
  return condition.problemListItem || false;
}

export function getEncounterDiagnosisId(
  condition: ConditionInputItem
): string | undefined {
  return condition.encounterDiagnosisId;
}

export function getProblemListItemId(
  condition: ConditionInputItem
): string | undefined {
  return condition.problemListItemId;
}

// =============================================================================
// SETTERS - Immutable updates
// =============================================================================

export function setConditionCode(
  condition: ConditionInputItem,
  code: CodeableConcept | undefined
): ConditionInputItem {
  return {
    ...condition,
    code,
  };
}

export function setClinicalStatus(
  condition: ConditionInputItem,
  clinicalStatus: CodeableConcept | undefined
): ConditionInputItem {
  return {
    ...condition,
    clinicalStatus,
  };
}

export function setOnsetDate(
  condition: ConditionInputItem,
  date: Date | null
): ConditionInputItem {
  return {
    ...condition,
    onsetDate: date,
  };
}

export function setNotes(
  condition: ConditionInputItem,
  notes: string
): ConditionInputItem {
  return {
    ...condition,
    notes,
  };
}

export function setProblemListItem(
  condition: ConditionInputItem,
  problemListItem: boolean
): ConditionInputItem {
  return {
    ...condition,
    problemListItem,
  };
}

// =============================================================================
// FACTORY - Create new ConditionInputItem
// =============================================================================

export function createNewConditionInputItem(): ConditionInputItem {
  return {
    encounterDiagnosisId: undefined,
    problemListItemId: undefined,
    code: undefined,
    clinicalStatus: {
      coding: [
        {
          system: HL7.TERMINOLOGY.CS.CONDITION.CLINICAL.BASE,
          code: HL7.TERMINOLOGY.CS.CONDITION.CLINICAL.CODES.ACTIVE,
          display: 'Ενεργή',
        },
      ],
    },
    problemListItem: false,
    onsetDate: null,
    notes: '',
  };
}

// =============================================================================
// HELPERS - Convenience functions
// =============================================================================

export function hasConditionCode(condition: ConditionInputItem): boolean {
  const code = getConditionCode(condition);
  return Boolean(code?.coding?.[0]?.code || code?.text);
}

export function hasClinicalStatus(condition: ConditionInputItem): boolean {
  const status = getClinicalStatus(condition);
  return Boolean(status?.coding?.[0]?.code);
}

export function mapConditionsToInputItems(
  conditions: Condition[]
): ConditionInputItem[] {
  const conditionInputs: ConditionInputItem[] = [];
  const conditionMatchMap = new Map<string, ConditionInputItem[]>();

  const buildMatchKey = (condition: Condition): string => {
    const coding = condition.code?.coding?.[0];
    const systemCode = coding
      ? `${coding.system ?? ''}|${coding.code ?? ''}`
      : (condition.code?.text ?? '');
    const onset = condition.onsetDateTime ?? '';
    const note = condition.note?.[0]?.text ?? '';
    return `${systemCode}|${onset}|${note}`;
  };

  // Separate diagnoses (encounter or medical history) and problem list items
  const diagnosisConditions = conditions.filter((condition) =>
    condition.category?.some((cat) =>
      cat.coding?.some(
        (coding) =>
          coding.code ===
            HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES.ENCOUNTER_DIAGNOSIS ||
          coding.code ===
            HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES.MEDICAL_HISTORY
      )
    )
  );

  const problemListConditions = conditions.filter((condition) =>
    condition.category?.some((cat) =>
      cat.coding?.some(
        (coding) =>
          coding.code ===
          HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES.PROBLEM_LIST_ITEM
      )
    )
  );

  // Process diagnoses first
  for (const diagnosis of diagnosisConditions) {
    const input: ConditionInputItem = {
      encounterDiagnosisId: diagnosis.id,
      problemListItemId: undefined,
      code: diagnosis.code,
      clinicalStatus: diagnosis.clinicalStatus,
      problemListItem: false,
      onsetDate: diagnosis.onsetDateTime
        ? new Date(diagnosis.onsetDateTime)
        : null,
      notes: diagnosis.note?.[0]?.text || '',
    };

    conditionInputs.push(input);

    const key = buildMatchKey(diagnosis);
    const matches = conditionMatchMap.get(key) ?? [];
    matches.push(input);
    conditionMatchMap.set(key, matches);
  }

  // Process problem list items and try to match with diagnoses
  for (const problemCondition of problemListConditions) {
    const key = buildMatchKey(problemCondition);
    const matches = conditionMatchMap.get(key);
    const availableMatch = matches?.find((match) => !match.problemListItem);

    if (availableMatch) {
      // Link to existing encounter diagnosis
      availableMatch.problemListItem = true;
      availableMatch.problemListItemId = problemCondition.id;
      // Ensure shared clinical status / notes stay aligned with encounter condition
      availableMatch.clinicalStatus =
        availableMatch.clinicalStatus ?? problemCondition.clinicalStatus;
      if (!availableMatch.notes && problemCondition.note?.[0]?.text) {
        availableMatch.notes = problemCondition.note?.[0]?.text || '';
      }
      continue;
    }

    // Standalone problem list item
    const input: ConditionInputItem = {
      encounterDiagnosisId: undefined,
      problemListItemId: problemCondition.id,
      code: problemCondition.code,
      clinicalStatus: problemCondition.clinicalStatus,
      problemListItem: true,
      onsetDate: problemCondition.onsetDateTime
        ? new Date(problemCondition.onsetDateTime)
        : null,
      notes: problemCondition.note?.[0]?.text || '',
    };

    conditionInputs.push(input);
  }

  return conditionInputs;
}

export function getClinicalStatusDisplay(
  condition: ConditionInputItem
): string | undefined {
  const status = getClinicalStatus(condition);
  return status?.text || status?.coding?.[0]?.display;
}

export function getConditionDisplay(
  condition: ConditionInputItem
): string | undefined {
  return getConditionCodeText(condition);
}

// =============================================================================
// PREPARATION - Prepare ConditionInputItem for saving
// =============================================================================

export function prepareConditionForSave(
  patient: Patient,
  encounter: Encounter,
  conditionInput: ConditionInputItem,
  isMedicalHistory: boolean = false
): {
  encounterDiagnosis: Condition;
  problemListItem?: Condition;
  shouldDeleteProblemListItem: boolean;
} {
  const commonFields: Pick<
    Condition,
    'code' | 'clinicalStatus' | 'verificationStatus' | 'onsetDateTime' | 'note'
  > = {
    code: conditionInput.code,
    clinicalStatus:
      conditionInput.clinicalStatus ??
      ({
        coding: [
          {
            system: HL7.TERMINOLOGY.CS.CONDITION.CLINICAL.BASE,
            code: HL7.TERMINOLOGY.CS.CONDITION.CLINICAL.CODES.ACTIVE,
            display: 'Active',
          },
        ],
      } as Condition['clinicalStatus']),
    verificationStatus: {
      coding: [
        {
          system: HL7.TERMINOLOGY.CS.CONDITION.VERIFICATION_STATUS.BASE,
          code: HL7.TERMINOLOGY.CS.CONDITION.VERIFICATION_STATUS.CODES
            .CONFIRMED,
          display: 'Confirmed',
        },
      ],
    },
    onsetDateTime: conditionInput.onsetDate
      ? conditionInput.onsetDate.toISOString().split('T')[0]
      : undefined,
    note: conditionInput.notes ? [{ text: conditionInput.notes }] : undefined,
  };

  // Encounter diagnosis category
  const encounterCategory = isMedicalHistory
    ? [
        {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.BASE,
              code: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES.MEDICAL_HISTORY,
              display: 'Medical History',
            },
          ],
          text: 'Medical History',
        },
      ]
    : [
        {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.BASE,
              code: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES
                .ENCOUNTER_DIAGNOSIS,
              display: 'Encounter Diagnosis',
            },
          ],
          text: 'Encounter Diagnosis',
        },
      ];

  const encounterDiagnosis: Condition = {
    resourceType: 'Condition',
    subject: createReference(patient),
    encounter: createReference(encounter),
    category: encounterCategory,
    ...commonFields,
    ...(conditionInput.encounterDiagnosisId && {
      id: conditionInput.encounterDiagnosisId,
    }),
  };

  let problemListItem: Condition | undefined;
  const shouldDeleteProblemListItem =
    !conditionInput.problemListItem && !!conditionInput.problemListItemId;

  // Problem list item (if needed)
  if (conditionInput.problemListItem) {
    const problemListCategory = isMedicalHistory
      ? [
          {
            coding: [
              {
                system: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.BASE,
                code: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES
                  .PROBLEM_LIST_ITEM,
                display: 'Problem List Item',
              },
            ],
            text: 'Problem List Item',
          },
          {
            coding: [
              {
                system: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.BASE,
                code: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES
                  .MEDICAL_HISTORY,
                display: 'Medical History',
              },
            ],
            text: 'Medical History',
          },
        ]
      : [
          {
            coding: [
              {
                system: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.BASE,
                code: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES
                  .PROBLEM_LIST_ITEM,
                display: 'Problem List Item',
              },
            ],
            text: 'Problem List Item',
          },
          {
            coding: [
              {
                system: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.BASE,
                code: HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES
                  .ENCOUNTER_DIAGNOSIS,
                display: 'Encounter Diagnosis',
              },
            ],
            text: 'Encounter Diagnosis',
          },
        ];
    problemListItem = addProfileToResource(
      {
        resourceType: 'Condition',
        subject: createReference(patient),
        encounter: createReference(encounter),
        category: problemListCategory,
        ...commonFields,
        ...(conditionInput.problemListItemId && {
          id: conditionInput.problemListItemId,
        }),
      },
      HL7.ORG.SD.CONDITION_PROBLEM
    );
  }

  return {
    encounterDiagnosis,
    problemListItem,
    shouldDeleteProblemListItem,
  };
}
// =============================================================================
// Convert a single FHIR Condition to ConditionInputItem for display purposes
// =============================================================================
export const conditionToInputItem = (
  condition: Condition
): ConditionInputItem => {
  return {
    encounterDiagnosisId: condition.id,
    problemListItemId: undefined,
    code: condition.code,
    clinicalStatus: condition.clinicalStatus,
    problemListItem: false,
    onsetDate: condition.onsetDateTime
      ? new Date(condition.onsetDateTime)
      : null,
    notes: condition.note?.[0]?.text || '',
  };
};
